/**
 * Deployment environments, on the general account page (task 760).
 *
 * Report: `project/reports/innovate-service-deployment-environments.md`.
 *
 * Placement is the teaching (kv's ruling): this sits DIRECTLY UNDER Publishing
 * and is inert until Publishing is on, because an environment is a pointer at a
 * published version — with nothing frozen there is nothing to point at, and a
 * managed URL would only 404. The adjacency is what makes the dependency
 * obvious without a paragraph explaining it.
 *
 * Always called a **deployment environment** in copy: these are the Account's
 * own stages, not the platform's local/preprod/prd infrastructure.
 *
 * **NAME AND PATH ARE TWO DIFFERENT THINGS (task 831).** Until task 830 the
 * name did both jobs — the label you read and the segment in the URL — so a
 * name had to be slug-safe and renaming an environment silently moved every
 * consumer's URL. Now:
 *
 *   - **Name** is a display label. Free text, no rules, and two stages may
 *     share one. Editing it changes nothing a caller can see.
 *   - **Path** is the address, and the only identity an environment has. Slug
 *     rules live here, and the special value `/` means *this environment has no
 *     segment*: the bare URL serves it, and at most one environment per Account
 *     may hold it.
 *
 * The retired `isDefault` boolean is why the row lost its **Make default**
 * button: "which environment does the bare URL serve" is no longer a flag to
 * set but an address to hold, so moving the bare URL is editing paths — take
 * `/` off the environment that has it, then give it to another. The path is
 * editable in the row for exactly that reason; without it the platform would
 * have no way at all to move the bare URL.
 */
import { useState } from 'react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
} from '@heroicons/react/24/outline';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useVersioningEnabled } from '@/hooks/useResourceVersions';
import {
  useCreateEnvironment,
  useDeleteEnvironment,
  useEnvironments,
  useUpdateEnvironment,
} from '@/hooks/useEnvironments';
import { useOptionalToast } from '@/contexts/ToastContext';
import { BARE_URL_ENVIRONMENT_PATH } from '@/types/environments';
import type { Environment } from '@/types/environments';
import DeleteResourceControl from '@/components/shared/DeleteResourceControl';

/**
 * Mirrors the service's write-time path rule (task 830's
 * `ENVIRONMENT_PATH_PATTERN`), so a bad path fails before the call. The bare-URL
 * path is exempt there and here: it occupies no segment, so the slug rules have
 * nothing to apply to.
 */
const PATH_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** The name is a label now — the only rule left is that there has to be one. */
const validateName = (name: string): string | null =>
  name.trim() ? null : 'Give the environment a name';

const validatePath = (path: string): string | null => {
  const trimmed = path.trim();
  if (!trimmed) return 'Give the environment a path';
  if (trimmed === BARE_URL_ENVIRONMENT_PATH) return null;
  if (!PATH_PATTERN.test(trimmed)) {
    return 'A path is "/" or lowercase letters, numbers and single hyphens — it becomes part of the URL';
  }
  return null;
};

/** Which field of which row is open for editing. */
interface RowEdit {
  id: string;
  field: 'name' | 'path';
  value: string;
}

export function EnvironmentsSection() {
  const featureEnabled = useFlag('innovate.fe.environments.manage');
  const { enabled: publishingEnabled } = useVersioningEnabled();
  const { environments, isLoading } = useEnvironments();
  const createEnvironment = useCreateEnvironment();
  const updateEnvironment = useUpdateEnvironment();
  const deleteEnvironment = useDeleteEnvironment();
  const toast = useOptionalToast();

  const [newName, setNewName] = useState('');
  const [newPath, setNewPath] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [pathError, setPathError] = useState<string | null>(null);
  const [editing, setEditing] = useState<RowEdit | null>(null);

  if (!featureEnabled) return null;

  const handleCreate = () => {
    const nameProblem = validateName(newName);
    const pathProblem = validatePath(newPath);
    setNameError(nameProblem);
    setPathError(pathProblem);
    if (nameProblem || pathProblem) return;

    createEnvironment.mutate(
      // No `order` (task 1134): the service places a new environment FIRST in
      // the release sequence, and the `/` holder is always last. Computing it
      // here used to put every new lane after Production.
      {
        name: newName.trim(),
        path: newPath.trim(),
      },
      {
        onSuccess: () => {
          setNewName('');
          setNewPath('');
          setNameError(null);
          setPathError(null);
        },
        onError: () => {
          // The service's own reasons, in the app's words: a path is unique per
          // account, shares a namespace with AI Service paths, and only one
          // environment may hold `/`.
          setPathError(
            'That path could not be used. It may already be taken, hold the bare URL already, or clash with an AI Service path.'
          );
        },
      }
    );
  };

  /** Save whichever field the row has open — the two writes differ only in it. */
  const handleSaveEdit = (environment: Environment) => {
    if (!editing || editing.id !== environment.id) return;
    const value = editing.value.trim();
    const problem =
      editing.field === 'name' ? validateName(editing.value) : validatePath(editing.value);
    if (problem) {
      toast?.error(editing.field === 'name' ? 'Could not rename' : 'Could not change the path', problem);
      return;
    }
    if (value === environment[editing.field]) {
      setEditing(null);
      return;
    }

    updateEnvironment.mutate(
      { id: environment.id, data: { [editing.field]: value } },
      {
        onSuccess: () => setEditing(null),
        onError: () =>
          toast?.error(
            editing.field === 'name' ? 'Could not rename' : 'Could not change the path',
            editing.field === 'name'
              ? 'The environment was not changed.'
              : 'The path may already be taken, may be held by another environment as the bare URL, or may clash with an AI Service path.'
          ),
      }
    );
  };

  /**
   * Reordering swaps two rows' `order` values rather than renumbering the list:
   * the sequence is what promotion means, so the smallest possible write is the
   * one that cannot leave a gap or a duplicate behind.
   */
  const handleMove = (index: number, direction: -1 | 1) => {
    const moving = environments[index];
    const neighbour = environments[index + direction];
    if (!moving || !neighbour) return;
    // The `/` holder is always last (task 1134); nothing swaps places with it.
    if (moving.path === BARE_URL_ENVIRONMENT_PATH || neighbour.path === BARE_URL_ENVIRONMENT_PATH) return;

    updateEnvironment.mutate({ id: moving.id, data: { order: neighbour.order } });
    updateEnvironment.mutate({ id: neighbour.id, data: { order: moving.order } });
  };

  /**
   * The environments that can move (task 1134): every one but the `/` holder,
   * which the list always shows last. They occupy the first `movableCount` rows.
   */
  const movableCount = environments.filter(
    (environment) => environment.path !== BARE_URL_ENVIRONMENT_PATH
  ).length;

  return (
    <section
      data-testid="environments-section"
      className="mt-6 rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="min-w-0">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Deployment environments
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Named stages a published version can be deployed to — <code>staging</code> before{' '}
          <code>prod</code>, in the order you promote through them. Each one serves its own version
          at its own URL.
        </p>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400" data-testid="environments-sequence-help">
          The order below is the release order: a version can be released to an environment only
          after it has been released to the one above it. New environments are added at the top,
          and the environment holding <code>/</code> is always last.
        </p>
      </div>

      {!publishingEnabled && (
        <div
          className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40"
          data-testid="environments-disabled-state"
        >
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <span className="font-medium text-gray-900 dark:text-white">
              Enable Publishing to use deployment environments.
            </span>{' '}
            An environment points at a published version, so there has to be something frozen for it
            to serve. Turn on Publishing &amp; versions above, then come back here.
          </p>
        </div>
      )}

      {publishingEnabled && isLoading && (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400" data-testid="environments-loading">
          Loading your deployment environments…
        </p>
      )}

      {publishingEnabled && !isLoading && (
        <div className="mt-4" data-testid="environments-enabled-state">
          {environments.length === 0 ? (
            <p className="text-sm text-gray-700 dark:text-gray-300" data-testid="environments-empty">
              No deployment environments yet. Most accounts start with <code>staging</code> and{' '}
              <code>prod</code>; you can add more later.
            </p>
          ) : (
            <ol className="divide-y divide-gray-200 dark:divide-gray-700" data-testid="environments-list">
              {environments.map((environment, index) => (
                <li
                  key={environment.id}
                  data-testid="environment-row"
                  className="flex items-center gap-3 py-3 first:pt-0"
                >
                  <span className="w-6 flex-shrink-0 text-xs text-gray-400 dark:text-gray-500">
                    {index + 1}
                  </span>

                  {/* THE LABEL. Free text since task 830 — clicking it edits
                      the name and nothing else; no URL moves. */}
                  {editing?.id === environment.id && editing.field === 'name' ? (
                    <input
                      value={editing.value}
                      onChange={(event) =>
                        setEditing({ ...editing, value: event.target.value })
                      }
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') handleSaveEdit(environment);
                        if (event.key === 'Escape') setEditing(null);
                      }}
                      autoFocus
                      aria-label={`Rename ${environment.name}`}
                      data-testid="environment-rename-input"
                      className="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() =>
                        setEditing({ id: environment.id, field: 'name', value: environment.name })
                      }
                      data-testid="environment-name"
                      title="Rename"
                      className="min-w-0 flex-1 truncate text-left text-sm text-gray-900 hover:underline dark:text-white"
                    >
                      {environment.name}
                    </button>
                  )}

                  {/* THE ADDRESS. Editing it moves traffic, which is why it is
                      shown in mono beside the label and never derived from it. */}
                  {editing?.id === environment.id && editing.field === 'path' ? (
                    <input
                      value={editing.value}
                      onChange={(event) =>
                        setEditing({ ...editing, value: event.target.value })
                      }
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') handleSaveEdit(environment);
                        if (event.key === 'Escape') setEditing(null);
                      }}
                      autoFocus
                      aria-label={`Change the path of ${environment.name}`}
                      data-testid="environment-path-input"
                      className="w-32 flex-shrink-0 rounded-md border border-gray-300 px-2 py-1 font-mono text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() =>
                        setEditing({ id: environment.id, field: 'path', value: environment.path })
                      }
                      data-testid="environment-path"
                      title="Change the path"
                      className="max-w-[10rem] flex-shrink-0 truncate rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-700 hover:underline dark:bg-gray-700 dark:text-gray-200"
                    >
                      /{environment.path === BARE_URL_ENVIRONMENT_PATH ? '' : environment.path}
                    </button>
                  )}

                  {environment.path === BARE_URL_ENVIRONMENT_PATH && (
                    <span
                      data-testid="environment-bare-url-badge"
                      className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
                    >
                      Bare URL
                    </span>
                  )}

                  {editing?.id === environment.id ? (
                    <button
                      type="button"
                      onClick={() => handleSaveEdit(environment)}
                      data-testid="environment-rename-save"
                      className="flex-shrink-0 text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                    >
                      Save
                    </button>
                  ) : (
                    <div className="flex flex-shrink-0 items-center gap-1">
                      {/* No move controls on the `/` holder (task 1134): it is
                          always the last lane, and the lane above it cannot move
                          past it. */}
                      {environment.path !== BARE_URL_ENVIRONMENT_PATH && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleMove(index, -1)}
                            disabled={index === 0}
                            aria-label={`Move ${environment.name} earlier`}
                            data-testid="environment-move-up"
                            className="rounded p-1 text-gray-400 hover:bg-gray-50 hover:text-gray-600 disabled:opacity-30 dark:hover:bg-gray-700"
                          >
                            <ArrowUpIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleMove(index, 1)}
                            disabled={index >= movableCount - 1}
                            aria-label={`Move ${environment.name} later`}
                            data-testid="environment-move-down"
                            className="rounded p-1 text-gray-400 hover:bg-gray-50 hover:text-gray-600 disabled:opacity-30 dark:hover:bg-gray-700"
                          >
                            <ArrowDownIcon className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      {/* The one delete affordance (task 864). The bespoke
                          full-screen modal this replaces was a near-copy of the
                          Custom Domains one, and neither knew about the Archive
                          a deleted environment now goes to.

                          `entity` points it at the lane's dependents route (task
                          870), which is what lets the dialog refuse before the
                          click when an AI Service is deployed here, name the
                          services that have deployed here before, and pass on
                          whatever else the service has to say about the
                          removal. */}
                      <DeleteResourceControl
                        flag="innovate.fe.environments.delete"
                        entity="environments"
                        entityLabel="deployment environment"
                        name={environment.name}
                        id={environment.id}
                        testId="environment-delete"
                        body={
                          <p>
                            Anything served at this environment&rsquo;s URL stops being reachable
                            there. Published versions themselves are untouched — only the pointer
                            goes away.
                          </p>
                        }
                        onDelete={() => deleteEnvironment.mutateAsync(environment.id)}
                      />
                    </div>
                  )}
                </li>
              ))}
            </ol>
          )}

          {/* TWO FIELDS, NOT ONE (task 831). The label and the address were the
              same input until task 830 split them, so the form splits too — and
              says what `/` means where it is typed, because "the path that is
              not a segment" is the one thing about this form nobody guesses. */}
          <div className="mt-4 flex flex-wrap items-start gap-2">
            <div className="min-w-0 flex-1">
              <label
                htmlFor="environment-new-name"
                className="block text-xs font-medium text-gray-700 dark:text-gray-300"
              >
                Name
              </label>
              <input
                id="environment-new-name"
                value={newName}
                onChange={(event) => {
                  setNewName(event.target.value);
                  if (nameError) setNameError(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') handleCreate();
                }}
                placeholder="Staging"
                aria-label="New deployment environment name"
                data-testid="environment-new-name"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                The label you read. Anything you like.
              </p>
              {nameError && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-testid="environment-name-error">
                  {nameError}
                </p>
              )}
            </div>

            <div className="min-w-0 flex-1">
              <label
                htmlFor="environment-new-path"
                className="block text-xs font-medium text-gray-700 dark:text-gray-300"
              >
                Path
              </label>
              <input
                id="environment-new-path"
                value={newPath}
                onChange={(event) => {
                  setNewPath(event.target.value);
                  if (pathError) setPathError(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') handleCreate();
                }}
                placeholder="staging"
                aria-label="New deployment environment path"
                data-testid="environment-new-path"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 font-mono text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-testid="environment-path-help">
                The URL segment: <code>/api/&#123;account&#125;/&#123;path&#125;/&#123;service&#125;</code>. Path{' '}
                <code>/</code> means this environment serves the bare URL with no segment — one
                environment at most.
              </p>
              {pathError && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-testid="environment-path-error">
                  {pathError}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={handleCreate}
              disabled={createEnvironment.isPending}
              data-testid="environment-create"
              className="mt-5 flex-shrink-0 rounded-md border border-transparent bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              Add environment
            </button>
          </div>
        </div>
      )}

    </section>
  );
}

export default EnvironmentsSection;
